import { useEffect, useMemo, useState } from "react";
import { Button } from "../../../../components/Button";
import { Checkbox } from "../../../../components/Checkbox";
import { TextInput } from "../../../../components/TextInput";
import { getOrganizations } from "../../../../services/admin";
import {
  distributeEvent,
  getDistributionRecipients,
  type DistributeResult,
  type DistributeTarget,
  type OrganizationSport,
} from "../../../../services/calendar";

/** Human labels for the per-org distribution outcome statuses the API returns. */
const STATUS_LABEL: Record<string, string> = {
  completed: "updated",
  skipped_locally_edited: "skipped — a coach edited it",
  skipped_deleted: "skipped — deleted there",
  failed: "failed",
};

const statusLabel = (status: string) => STATUS_LABEL[status] ?? status;

/**
 * How the recipients are chosen. The three "everyone who…" modes and the explicit picker are one
 * exclusive choice, because the server accepts exactly one targeting mode and silently ignoring
 * the others would read to an admin as a successful share to the wrong set of programs.
 */
type DistributionMode = "all" | OrganizationSport | "select";

const SPORT_MODES: { value: OrganizationSport; label: string; action: string }[] = [
  { value: "baseball", label: "All baseball programs", action: "Distribute to all baseball programs" },
  { value: "softball", label: "All softball programs", action: "Distribute to all softball programs" },
];

const isSportMode = (mode: DistributionMode): mode is OrganizationSport =>
  mode === "baseball" || mode === "softball";

interface EventDistributionPanelProps {
  eventId: string;
}

/**
 * System-admin action: push a copy of this event onto other orgs' calendars. Searchable org picker
 * (all vs. select). Re-running updates existing copies and SKIPS any a recipient has edited locally
 * (surfaced per-org in the result), so a coach's local fixes are never clobbered.
 */
export function EventDistributionPanel({ eventId }: EventDistributionPanelProps) {
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  const [mode, setMode] = useState<DistributionMode>("all");
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [distributing, setDistributing] = useState(false);
  const [result, setResult] = useState<DistributeResult | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getOrganizations({ pageSize: 200 })
      .then((resp) => {
        if (active) setOrgs(resp.items.map((o) => ({ id: o.id, name: o.name })));
      })
      .catch(() => {
        /* non-fatal: the picker just shows no options */
      });
    return () => {
      active = false;
    };
  }, []);

  // Pre-select the orgs this event was previously distributed to, so opening
  // "Select orgs" starts with the last recipients checked (re-push the same set
  // after an edit). Default mode stays "all"; this only seeds the selection.
  useEffect(() => {
    let active = true;
    getDistributionRecipients(eventId)
      .then((ids) => {
        if (active && ids.length > 0) setSelected(ids);
      })
      .catch(() => {
        /* non-fatal: picker just starts empty */
      });
    return () => {
      active = false;
    };
  }, [eventId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? orgs.filter((o) => o.name.toLowerCase().includes(q)) : orgs;
  }, [orgs, search]);

  const run = async () => {
    if (mode === "select" && selected.length === 0) {
      setError("Pick at least one organization, or choose all organizations.");
      return;
    }
    setDistributing(true);
    setError("");
    setResult(null);
    try {
      // A sport share targets only orgs whose recorded sport MATCHES, so an org with no sport on
      // file is deliberately in neither group rather than quietly in both.
      const target: DistributeTarget =
        mode === "all"
          ? { allOrganizations: true }
          : isSportMode(mode)
            ? { allOrganizations: false, sport: mode }
            : { allOrganizations: false, organizationIds: selected };
      const res = await distributeEvent(eventId, target);
      setResult(res);
    } catch {
      setError("Couldn't distribute the event. Try again.");
    } finally {
      setDistributing(false);
    }
  };

  return (
    <div className="event-view__section admin-distribute" data-testid="distribute-panel">
      <div className="admin-distribute__head">🛡️ Distribute to organizations</div>
      <div className="admin-distribute__seg" role="group" aria-label="Who receives this event">
        <button
          type="button"
          className={mode === "all" ? "on" : ""}
          aria-pressed={mode === "all"}
          onClick={() => setMode("all")}
        >
          All organizations
        </button>
        {SPORT_MODES.map((sport) => (
          <button
            key={sport.value}
            type="button"
            className={mode === sport.value ? "on" : ""}
            aria-pressed={mode === sport.value}
            onClick={() => setMode(sport.value)}
            data-testid={`distribute-sport-${sport.value}`}
          >
            {sport.label}
          </button>
        ))}
        <button
          type="button"
          className={mode === "select" ? "on" : ""}
          aria-pressed={mode === "select"}
          onClick={() => setMode("select")}
        >
          Select orgs
        </button>
      </div>

      {mode === "select" && (
        <>
          <TextInput
            className="admin-distribute__search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search organizations"
            aria-label="Search organizations"
            data-testid="distribute-search"
          />
          <div className="admin-distribute__orgs">
            {filtered.length === 0 ? (
              <div className="event-editor__hint">No organizations match.</div>
            ) : (
              filtered.map((o) => (
                <Checkbox
                  key={o.id}
                  className="admin-distribute__org"
                  label={o.name}
                  checked={selected.includes(o.id)}
                  onChange={(e) =>
                    setSelected(
                      e.target.checked
                        ? [...selected, o.id]
                        : selected.filter((x) => x !== o.id),
                    )
                  }
                />
              ))
            )}
          </div>
        </>
      )}

      <Button
        type="button"
        variant="primary"
        fullWidth
        onClick={run}
        disabled={distributing}
        data-testid="distribute-run"
      >
        {distributing
          ? "Distributing…"
          : mode === "all"
            ? "Distribute to all orgs"
            : isSportMode(mode)
              ? (SPORT_MODES.find((s) => s.value === mode)?.action ?? "Distribute")
              : `Distribute to ${selected.length} org(s)`}
      </Button>

      {error && <div className="event-editor__error">{error}</div>}

      {result && (
        <div className="admin-distribute__result" data-testid="distribute-result">
          <div>
            {result.distributed} distributed · {result.skipped} skipped · {result.failed} failed
          </div>
          {result.results.length > 0 && (
            <ul className="admin-distribute__result-list">
              {result.results.map((r) => (
                <li key={r.organizationId}>
                  {r.organizationName ?? r.organizationId}: {statusLabel(r.status)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="event-editor__hint">
        Puts a copy of this event on each org's calendar. Re-running updates existing copies and
        skips any a coach has edited locally. A sport share reaches only programs with that sport
        recorded, so an organization with no sport on file receives nothing.
      </div>
    </div>
  );
}
